import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import {
    MyCouponCard,
    KidsLoading,
    useTheme,
    NoData,
    isSmallDevice,
    withAppBottomSheet,
    type BottomSheetExtraProps,
} from '../../../../utils';
import { useCouponWidgetContent } from '../../../../api/requests/coupont.request';
import type { CouponContentResponse } from '../../../../types/front.graphql';
import { useAddCouponToWallet } from '../../hooks/useAddCouponToWallet';
import type { CouponListContentProps } from './coupon-list-screen.types';
import { useSortedCoupons } from '../../hooks/useSortedCoupons';
import { COUPON_CONSTANTS } from '../../../../constant/coupon-constant';
import { useShowCouponDetails } from '../../hooks/useShowCouponDetail';
import { NetworkStatus } from '@apollo/client';
import { cn, Divider, Fragment, Text } from '@kidslabs/mario-nextjs-react-ui-core';
import styles from './coupon-list-screen.module.css';
import { getCouponImageUri } from '@kidslabs/module-core';

// Web stub for useIsFocused
const useIsFocused = () => {
    const [isFocused, setIsFocused] = React.useState(true);
    React.useEffect(() => {
        const handleVisibilityChange = () => setIsFocused(document.visibilityState === 'visible');
        if (typeof document !== 'undefined') {
            document.addEventListener('visibilitychange', handleVisibilityChange);
            return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
        }
        return undefined;
    }, []);
    return isFocused;
};

// Web-compatible screen props type
export interface CouponListProps {
    isMobile?: boolean;
    route?: {
        params?: {
            input?: unknown;
        };
    };
}

const CouponListContent: React.FC<BottomSheetExtraProps & CouponListContentProps> = props => {
    const isFocused = useIsFocused();
    const { t } = useTranslation('flash-sale');
    useTheme();

    const getProgressLabel = useCallback(
        (coupon?: {
            salesrule?: {
                usage_limit?: number;
                times_used?: number;
            };
        }) => {
            const usageLimit = coupon?.salesrule?.usage_limit || 0;
            const timesUsed = coupon?.salesrule?.times_used || 0;

            if (!usageLimit) return null;

            const progressValue = Math.round((timesUsed / usageLimit) * 100);
            return t('text_used_progress', { percent: progressValue });
        },
        [t]
    );

    const { bottomSheet, couponInput, footerContent, limitItem, containerStyle, isMobile } = props;
    const cardVariant = COUPON_CONSTANTS.DEFAULT_VARIANT;

    const { couponContent, networkStatus } = useCouponWidgetContent({
        filter: couponInput,
    });

    const { sortedCoupons, markAdding } = useSortedCoupons({
        coupons: couponContent,
        isFocused,
        limitItem,
    });
    const renderFooter = useMemo(() => {
        if (!limitItem) {
            return null;
        }
        return limitItem < (couponContent?.length ?? 0) ? (footerContent ?? null) : null;
    }, [limitItem, couponContent, footerContent]);

    const { handleAddCoupon, getActionLabel, isAdding } = useAddCouponToWallet();

    const showCouponDetail = useShowCouponDetails(bottomSheet, { containerStyle: {} }, t, props);

    const handleAddCouponToWallet = useCallback(
        (item: CouponContentResponse) => {
            markAdding();
            handleAddCoupon(item);
        },
        [handleAddCoupon, markAdding]
    );

    const renderCouponItem = useCallback(
        (item: CouponContentResponse) => {
            const couponId = item?.salesrule?.coupon_id ?? '';
            // Map CouponContentResponse to MyCouponContent format
            const imagePartner = couponInput?.partner ? item.partner : undefined;

            const couponData = {
                name: item.name ?? '',
                tag: item.tag ?? undefined,
                salesrule: item.salesrule
                    ? {
                          usage_limit: item.salesrule.usage_limit ?? undefined,
                          times_used: item.salesrule.times_used ?? undefined,
                          from_date: item.salesrule.from_date ?? undefined,
                          to_date: item.salesrule.to_date ?? undefined,
                      }
                    : undefined,
                imageUri: getCouponImageUri({
                    partner: imagePartner ?? (item.image ? [{ image: item.image }] : undefined),
                    brand_image: item.brand_image,
                }),
            };
            const hasShowProgress = !couponInput?.partner;
            const onPressGetCode = couponInput?.partner ? undefined : handleAddCouponToWallet.bind(null, item);
            return (
                <MyCouponCard
                    coupon={couponData}
                    labelButton={t('coupon_conditions')}
                    expiredLabel={t('coupon_activate')}
                    variant={cardVariant}
                    hasShowProgress={hasShowProgress}
                    onPressGetCode={onPressGetCode}
                    onPressShowMore={() => showCouponDetail(item, couponInput?.partner ? 'eWalletVoucher' : 'voucher')}
                    status={item.is_in_wallet ?? false}
                    codeLabel={getActionLabel(item)}
                    isAdding={isAdding(couponId)}
                    progressLabel={getProgressLabel(couponData)}
                />
            );
        },
        [
            showCouponDetail,
            t,
            getActionLabel,
            cardVariant,
            isAdding,
            handleAddCouponToWallet,
            couponInput,
            getProgressLabel,
        ]
    );

    const containerClassName = useMemo(
        () =>
            cn(
                isMobile ? styles.mobileContainer : styles.desktopContainer,
                typeof containerStyle === 'string' ? containerStyle : undefined
            ),
        [isMobile, containerStyle]
    );

    const listClassName = useMemo(() => cn(isMobile ? styles.mobileList : styles.desktopList), [isMobile]);

    if (networkStatus === NetworkStatus.loading || networkStatus === NetworkStatus.setVariables) {
        return (
            <div className={styles.loadingContainer}>
                <KidsLoading />
            </div>
        );
    }

    if (isMobile) {
        return (
            <div className={containerClassName}>
                <div className={listClassName}>
                    {sortedCoupons?.length ? (
                        sortedCoupons.map((item, index) => {
                            const key = item?.salesrule?.salesrule_id?.toString() ?? `${index}`;
                            const isLastItem = index === sortedCoupons.length - 1;

                            return (
                                <Fragment key={key}>
                                    {renderCouponItem(item)}
                                    {!isLastItem && (
                                        <Divider
                                            orientation="horizontal"
                                            thickness={1}
                                            className={styles.mobileDivider}
                                        />
                                    )}
                                </Fragment>
                            );
                        })
                    ) : (
                        <NoData />
                    )}
                </div>

                {renderFooter}
            </div>
        );
    }

    return (
        <div className={styles.backgroundLayout}>
            <div className={containerClassName}>
                <div className={styles.layoutDesktop}>
                    <Text className={styles.title}>{t('text_coupon_offers')}</Text>
                    <div className={listClassName}>
                        {sortedCoupons?.length ? (
                            sortedCoupons.map((item, index) => (
                                <div
                                    key={item?.salesrule?.salesrule_id?.toString() ?? `${index}`}
                                    className={styles.desktopItem}
                                >
                                    {renderCouponItem(item)}
                                </div>
                            ))
                        ) : (
                            <NoData />
                        )}
                    </div>

                    {renderFooter}
                </div>
            </div>
        </div>
    );
};

export const CouponListScreenContent = withAppBottomSheet(CouponListContent);

export const CouponListScreen: React.FC<CouponListProps> = ({ route, isMobile }) => {
    const snapPoints = useMemo(
        () =>
            isSmallDevice() ? COUPON_CONSTANTS.SMALL_DEVICE_SNAP_POINTS : COUPON_CONSTANTS.REGULAR_DEVICE_SNAP_POINTS,
        []
    );

    const couponInput = route?.params?.input;

    // @ts-expect-error - Type mismatch between route params and CouponWidgetFilterInput
    return <CouponListScreenContent snapPoints={[...snapPoints]} couponInput={couponInput} isMobile={isMobile} />;
};
